/**
 * Form Submission Router
 * Routes form submissions to appropriate handlers based on step type and edit state
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { STEP_NAMES } from '../constants/steps';
import {
  addCompletedStep,
  addMessage,
  removeSkippedStep,
} from '../data-store/copilotSlice';
import { showError } from '../services/messageService';

import { handleAccountSubmit } from './accountSubmissionUtils';
import { handleConnectionSubmit } from './connectionSubmissionUtils';
import { isAccountStep } from './stepMappingUtils';

/**
 * Handle form submission for previous step edits (no transitions)
 */
const handlePreviousStepSubmit = (
  dispatch: Dispatch,
  stepName: string,
  formData: Record<string, unknown>,
  isValid: boolean,
  getState: () => unknown,
  services?: {
    updateApplication: (payload: unknown) => Promise<unknown>;
  }
): Promise<void> => {
  if (stepName === STEP_NAMES.GENERAL && services) {
    // Handle General step edit with API call
    if (!isValid) {
      showError(dispatch, 'Please check general configuration details');

      return Promise.resolve();
    }

    const state = getState() as {
      copilot?: {
        endpointKey?: number;
        ui?: { endpointKey?: string | null };
      };
    };
    const endpointKey =
      state.copilot?.ui?.endpointKey || state.copilot?.endpointKey;

    // Make API call if endpointKey exists (edit mode)
    if (endpointKey) {
      // Extract ID from ownerkey
      let ownerKeyValue: unknown = formData['endpoints.ownerkey'];

      if (Array.isArray(ownerKeyValue) && ownerKeyValue.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const firstOwnerKey = ownerKeyValue[0];

        if (
          typeof firstOwnerKey === 'object' &&
          firstOwnerKey !== null &&
          'id' in firstOwnerKey
        ) {
          ownerKeyValue = (firstOwnerKey as { id: string }).id;
        } else if (typeof firstOwnerKey === 'string') {
          try {
            const parsed = JSON.parse(firstOwnerKey) as { id?: string };

            ownerKeyValue = parsed.id ?? String(firstOwnerKey);
          } catch {
            ownerKeyValue = firstOwnerKey;
          }
        }
      } else if (typeof ownerKeyValue === 'string') {
        if (ownerKeyValue.startsWith('[')) {
          try {
            const parsed = JSON.parse(ownerKeyValue) as { id?: string }[];

            ownerKeyValue = parsed[0]?.id || ownerKeyValue;
          } catch {
            // Keep original value if parsing fails
          }
        } else {
          try {
            const parsed = JSON.parse(ownerKeyValue) as { id?: string };

            ownerKeyValue = parsed.id || ownerKeyValue;
          } catch {
            // Keep as string if not JSON
          }
        }
      } else if (
        typeof ownerKeyValue === 'object' &&
        ownerKeyValue !== null &&
        'id' in ownerKeyValue
      ) {
        ownerKeyValue = (ownerKeyValue as { id: string }).id;
      }

      // Ensure ownerType is array
      let ownerTypeArray = formData['endpoints.ownerType'];

      if (!Array.isArray(ownerTypeArray)) {
        ownerTypeArray = [ownerTypeArray];
      }

      return services
        .updateApplication({
          applicationtype: 'CUA',
          endpointKey: Number(endpointKey),
          FormData: {
            multipleconnection: false,
            'externalconnectiontype.connectionType': 'CUA',
            'endpoints.description': String(
              formData['endpoints.description'] || ''
            ),
            'endpoints.ownerType': ownerTypeArray,
            'endpoints.ownerkey': String(ownerKeyValue || ''),
          },
        })
        .then(() => {
          dispatch(addCompletedStep(stepName));
          dispatch(removeSkippedStep(stepName));
          dispatch(
            addMessage({
              id: `success-${Date.now()}`,
              type: 'ai_response',
              subType: 'success',
              text: 'Application details updated successfully.',
              timestamp: Date.now(),
            })
          );
        })
        .catch(() => {
          dispatch(
            addMessage({
              id: `error-${Date.now()}`,
              type: 'ai_response',
              subType: 'error',
              timestamp: Date.now(),
            })
          );
        });
    }

    return Promise.resolve();
  }

  if (stepName === STEP_NAMES.CONNECTION) {
    handleConnectionSubmit(dispatch, formData, isValid, getState);

    return Promise.resolve();
  }

  if (isAccountStep(stepName)) {
    const accountStepName = stepName as
      | 'CREATE_ACCOUNT'
      | 'UPDATE_ACCOUNT'
      | 'ENABLE_ACCOUNT'
      | 'DISABLE_ACCOUNT'
      | 'REMOVE_ACCOUNT';

    return handleAccountSubmit(
      dispatch,
      accountStepName,
      formData,
      isValid,
      getState
    );
  }

  return Promise.resolve();
  // Note: No step completion or navigation for previous step edits
};

export default handlePreviousStepSubmit;
